import Seo, { pageSeo } from "../components/Seo";
import React from "react";
import { Link } from "react-router-dom";
import { Target, Eye, Award } from "lucide-react";
import { PageBanner, Reveal, SectionTitle, LucideIcon } from "../components/Shared";
import { company, values, team, stats, facilityPhotos } from "../mock";

const About = () => (
  <div data-testid="about-page">
    <Seo {...pageSeo.about} />
    <PageBanner
      title="About Us"
      subtitle="A Telangana-based manufacturer of printing inks and industrial coatings, built on colour precision and lasting quality."
      image={facilityPhotos.companyFront.src}
      crumbs={["About Us"]}
    />

    {/* Company background */}
    <section className="section bg-white">
      <div className="container-dic grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <p className="eyebrow">Company Background</p>
          <h2 className="section-title text-left">Colors That Speak, Coatings That Last.</h2>
          <div className="mt-6 space-y-4 text-[15px] leading-7 text-[#58595b] text-justify">
            <p>
              {company.name} was founded with a simple conviction: that printers and manufacturers deserve inks and coatings that
              perform consistently, batch after batch. From our facility at Medchal, Telangana, we formulate and manufacture
              offset, flexographic and gravure inks, overprint varnishes, packaging coatings and protective industrial finishes.
            </p>
            <p>
              Our production house combines modern dispersion and milling equipment with the hands-on expertise of chemists and
              colourists who understand real press conditions. A dedicated colour laboratory matches shades to brand standards,
              and every batch is tested and traceable before it leaves our delivery unit.
            </p>
            <p>
              Backed by a well-stocked warehouse, we are proud to serve packaging converters, publishers, label printers and
              engineering companies - accepting bulk orders and emergency requirements with equal commitment.
            </p>
          </div>
          <Link to="/contact" className="btn-green mt-8" data-testid="about-reach-us">Reach Us</Link>
        </Reveal>
        <Reveal delay={120} className="relative">
          <img src={facilityPhotos.companySide.src} alt={facilityPhotos.companySide.alt} className="rounded-md shadow-lg w-full h-[420px] object-cover" />
          <img src={facilityPhotos.productionHouse2.src} alt={facilityPhotos.productionHouse2.alt} className="hidden md:block absolute -bottom-8 -left-8 w-56 h-40 object-cover rounded-md shadow-xl border-4 border-white" />
          <span className="absolute -top-3 -right-3 w-24 h-24 bg-[#8cc63f]/20 rounded-full -z-10" />
        </Reveal>
      </div>
    </section>

    {/* Mission / Vision / Promise */}
    <section className="section bg-[#f7f7f7]" id="mission">
      <div className="container-dic">
        <Reveal><SectionTitle>Mission, Vision &amp; Promise</SectionTitle></Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Target, title: "Our Mission", text: "To deliver inks and coatings that reproduce colour faithfully and protect surfaces durably, helping our customers print and build with confidence." },
            { icon: Eye, title: "Our Vision", text: "To be South India's most trusted independent inks and coatings partner - known for precision, responsiveness and responsible chemistry." },
            { icon: Award, title: "Our Promise", text: "Consistent shade, dependable performance, honest advice and delivery when you need it - including bulk and emergency orders." },
          ].map(({ icon: Icon, title, text }, i) => (
            <Reveal key={title} delay={i * 100} className="card-plain">
              <div className="industry-icon mx-0"><Icon size={26} /></div>
              <h5 className="mt-5 text-[18px] font-bold text-[#231f20]">{title}</h5>
              <p className="mt-3 text-[14.5px] leading-7 text-[#58595b] text-justify">{text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Values */}
    <section className="section bg-white">
      <div className="container-dic">
        <Reveal><SectionTitle>What Sets Us Apart</SectionTitle></Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 80} className="text-center">
              <div className="industry-icon"><LucideIcon name={v.icon} size={26} /></div>
              <h6 className="mt-5 text-[16px] font-bold text-[#231f20]">{v.title}</h6>
              <p className="mt-2 text-[13.5px] leading-6 text-[#58595b]">{v.text}</p>
            </Reveal>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-14">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 80} className="stat">
              <div className="stat__value">{s.value}</div>
              <div className="stat__label">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Team */}
    <section className="section bg-[#f7f7f7]" id="team">
      <div className="container-dic">
        <Reveal><SectionTitle sub="A compact, experienced team that owns every stage from raw material to delivery.">Our Team</SectionTitle></Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {team.map((m, i) => (
            <Reveal key={m.name} delay={i * 90} className="card-plain text-center">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#8cc63f] to-[#38b6b0] flex items-center justify-center text-white text-[26px] font-bold">
                {m.name.charAt(0)}
              </div>
              <h6 className="mt-5 text-[15px] font-bold text-[#231f20]">{m.name}</h6>
              <p className="text-[12px] tracking-wide uppercase text-[#7ab929] font-semibold mt-1">{m.role}</p>
              <p className="mt-3 text-[13.5px] leading-6 text-[#58595b]">{m.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="section-sm bg-[#8cc63f]">
      <div className="container-dic flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h3 className="text-white text-[26px] font-bold">Looking for a reliable inks &amp; coatings partner?</h3>
          <p className="text-white/90 mt-1">Call {company.phoneDisplay} or send us your requirement.</p>
        </div>
        <Link to="/contact" className="btn-white" data-testid="about-cta-contact">Reach Us</Link>
      </div>
    </section>
  </div>
);

export default About;
